<script setup lang="ts">
import { ref } from "vue";
import { useRegisterSW } from "virtual:pwa-register/vue";
import { useIntersectionObserver } from "@vueuse/core";

const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW();

const close = async () => {
  offlineReady.value = false;
  needRefresh.value = false;
};

const banner = ref<HTMLDivElement>();
const showFooterTeaser = ref(false);
useIntersectionObserver(banner, entries => {
  if (entries && entries.length > 0) {
    const temp = entries[0];
    showFooterTeaser.value = !temp.isIntersecting;
  }
});
</script>

<template>
  <div
    v-if="offlineReady || needRefresh"
    ref="banner"
    class="pwa-toast"
    role="alert"
    data-cy="service-worker-prompt"
  >
    <div class="container flexed">
      <div class="message" data-cy="message">
        <span v-if="offlineReady" data-cy="offline-message"
          >App ready to work offline</span
        >
        <span v-else data-cy="refresh-message">
          A new version of this app is available, save your work first, then
          click on 'reload' button to update.
        </span>
      </div>
      <div class="buttons">
        <button
          v-if="needRefresh"
          class="p-button p-button-info"
          data-cy="reload-button"
          @click="updateServiceWorker()"
        >
          Reload
        </button>
        <button
          class="p-button p-button-danger"
          data-cy="close-button"
          @click="close"
        >
          Close
        </button>
      </div>
    </div>
    <div v-if="showFooterTeaser" class="teaser" data-cy="footer-teaser">
      <a @click="banner?.scrollIntoView({ behavior: 'smooth' })">
        <icon :icon="['fas', 'sparkles']" class="mr-2" />
        Update available
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pwa-toast {
  transition: all 0.25s ease-in-out;
  padding: 2em 0;
  background: var(--warning-500);
  box-shadow: var(--shadow-main);
  color: var(--warning-900);

  .flexed {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 0.5em;
    .message {
      padding: 0.5em;
    }

    .buttons {
      padding: 0.5em;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      gap: 0.5em;
    }
  }

  .teaser {
    a {
      color: var(--warning-900);
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: center;
    }

    z-index: 1;
    position: fixed;
    bottom: 0;
    right: 0;
    margin: 2em;
    background: var(--warning-500);
    padding: 1em 0.5em;
    box-shadow: var(--shadow-main);
    border: 1px solid var(--warning-700);
    border-radius: var(--border-radius);
  }
}
</style>
